<template>
  <div class="fts-import-designer">
    <div class="fts-row gutters" v-if="!userImport">
      <!-- test -->
      <div class="fts-col fts-col-md-4">
        <fts-form-element
          :form-data="formData"
          :item="{
            fieldName: 'test',
            input: 'toggleSwitch',
          }"
          comfort-design
        />
      </div>
      <!-- preprocess -->
      <div class="fts-col fts-col-md-4">
        <fts-form-element
          :form-data="formData"
          :item="{
            fieldName: 'preprocess',
            input: 'toggleSwitch',
          }"
          comfort-design
        />
      </div>
      <!-- initial state -->
      <div class="fts-col fts-col-md-4">
        <fts-form-element
          :form-data="formData"
          :item="{
            fieldName: 'initialStateId',
            displayName: 'Initial State',
            icon: 'fts-icon-menu-caret-down',
          }"
          comfort-design
        >
          <fts-select
            slot="formElement"
            slot-scope="{ formData, item }"
            :form-data="formData"
            :item="item"
            :options="states"
            comfort-design
          >
            <fts-state slot="selected-option-container" slot-scope="{option}" :state="option" />
            <fts-state slot="option" slot-scope="{option}" :state="option" />
          </fts-select>
        </fts-form-element>
      </div>

      <!-- to merge -->
      <!-- <div class="fts-col fts-col-md-4">
        <fts-form-element
          :form-data="formData"
          :item="{
            fieldName: 'preprocess',
            input: 'toggleSwitch',
          }"
          comfort-design
        />
      </div>-->

      <div class="fts-col fts-col-md-4">
        <fts-form-element
          :form-data="formData"
          :item="{
            fieldName: 'merge',
            displayName: 'Merge entry with the same information',
            icon: 'fts-icon-menu-caret-down',
          }"
          comfort-design
        >
          <fts-select
            slot="formElement"
            slot-scope="{ formData, item }"
            :form-data="formData"
            :item="item"
            label="displayName"
            :reduce="(x) => x"
            :options="mergeInformationSelection"
            comfort-design
          >
            <div slot="selected-option-container" slot-scope="{option}">{{option.displayName}}</div>
            <div slot="option" slot-scope="{option}">{{option.displayName}}</div>
          </fts-select>
        </fts-form-element>
      </div>
    </div>
    <!-- upload -->
    <div class="fts-form-group">
      <fts-form-label icon="fts-icon-file" placement="top">Import File</fts-form-label>
      <fts-file-upload
        :item="{
          displayName: 'Import Excel (.xlsx)',
          fieldName: 'importExcel',
        }"
        :form-data="formData"
        :additional-form-data="{ 'import': 'import' }"
        @fileUploaded="fileUploaded"
      />
      <fts-btn
        sm
        outline
        color="primary"
        text-transform-init
        :loading="uploading"
        class="import-refresh-btn"
        @click="refreshData"
        v-if="formData.importExcel && formData.importExcel.path"
      >
        Refresh Data
      </fts-btn>
    </div>
    <!-- column setup -->
    <div class="fts-form-group" v-if="!userImport">
      <fts-form-label icon="fts-icon-list-table" placement="top">Spreadsheet Information Mapping</fts-form-label>
      <fts-table-edit
        :item="headerColumns"
        :form-data="formData"
        :vector-id="vectorId"
        class="compact-form-design"
      />
    </div>
    <!-- excel data -->
    <div class="fts-form-group">
      <fts-form-label icon="fts-icon-list-table" placement="top">Spreadsheet Data</fts-form-label>
      <table class="fts-table-edit">
        <thead>
          <tr>
            <th v-for="column in spreadsheetDataColumns" :key="column.fieldName">
              <div>
                <div>
                  {{column.displayName}}
                  <span v-if="column.fieldName != 'error'">(Spreadsheet)</span>
                </div>
                <div style="color: orange;">
                  {{column.information}}
                  <span v-if="column.fieldName != 'error'">(Information)</span>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in importData.rows" :key="index">
            <td v-for="column in spreadsheetDataColumns" :key="column.id">
              <fts-display class="form-display-borderless" :item="column" :formData="row"></fts-display>
              <span v-if="formData.merge && column.update && row.isUpdate">(To be updated)</span>
              <span v-if="column.merge && row.isUpdate">(Found)</span>
              <span v-if="row[column.fieldName + 'Id']">
                <font-awesome-icon class="success-theme" :icon="['fas', 'check']" fixed-width />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- import script -->
    <div class="fts-form-group" v-if="!userImport && isBeta">
      <fts-script-editor
        editor-id="scriptEditor"
        lang="python"
        label="Import Script"
        v-model="formData.script"
      >
        <template #label="{ label }">
          {{label}} <fts-release-badge class="fts-align-top" />
        </template>
      </fts-script-editor>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Vuex from "vuex";
Vue.use(Vuex);

import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import { clientStore } from '../store/clientStore';
import { classificationStore } from '../store/classificationStore';
import { countryCode, normalizePhoneNumber } from '../commons/phoneNumber';
import { columnsAfterImport } from '../../business/importColumns';

import FTSForm from '../elements/FTSForm';
import FTSFormLabel from '../elements/FTSFormLabel';
import FTSFormElement from '../elements/FTSFormElement';
import FTSBtn from '../elements/FTSBtn';
import FTSDisplay from '../elements/FTSDisplay';
import FTSFileUpload from '../elements/FTSFileUpload';
import FTSScriptEditor from './FTSScriptEditor';
import FTSSelect from '../elements/FTSSelect';
import FTSState from '../elements/FTSState';
import FTSTableEdit from '../elements/FTSTableEdit';

export default {
  props: ["baseServer", "vectorId", "formData", "states", "userImport", "importing"],
  components: {
    "fts-form": FTSForm,
    "fts-form-label": FTSFormLabel,
    "fts-form-element": FTSFormElement,
    "fts-btn": FTSBtn,
    "fts-display": FTSDisplay,
    "fts-file-upload": FTSFileUpload,
    "fts-script-editor": FTSScriptEditor,
    "fts-select": FTSSelect,
    "fts-state": FTSState,
    "fts-table-edit": FTSTableEdit
  },
  data: () => ({
    uploading: false,
    informationSelection: [],
    mergeInformationSelection: [],
    spreadsheetDataColumns: [],
    headerColumns: {
      fieldName: "columns",
      fields: [
        {
          id: "header",
          fieldName: "header",
          displayName: "Spreadsheet Header",
          input: "text",
          isReadyOnly: true
        },
        {
          id: "information",
          fieldName: "information",
          displayName: "Module Information",
          input: "select",
          displayProperty: "displayName",
          key: "informationId",
          optionList: []
        },
        {
          id: "update",
          fieldName: "update",
          displayName: "Update Value",
          input: "boolean"
        },
        {
          id: "format",
          fieldName: "format",
          displayName: "Spreadsheet Format (if any special)",
          input: "text"
        }
      ]
    },
    columns: {
      fieldName: "rows",
      fields: []
    },
    importData: {
      rows: []
    }
  }),
  store: classificationStore,
  methods: {
    async import() {
      // const chunk = (list, size) => [...Array(Math.ceil(list.length / size))].map((_, i) => list.slice(size * i, size + size * i));
      // try {
      //   this.$emit("update:importing", true);
      //   await chunk(this.importData.rows, 100).reduce(async (accumulatorPromise, item, index) => {
      //     await accumulatorPromise;
      //     return new Promise(async (resolve, reject) => {
      //       try {
      //         await this.$store.dispatch("importData", {
      //           vectorId: this.vectorId,
      //           formData: this.formData,
      //           rows: item,
      //           submit: true
      //         });
      //         resolve(true)
      //       } catch(err) {
      //         reject(err);
      //       }
      //     });
      //   }, Promise.resolve());
      //   this.$emit("import:success");
      //   this.$toasted.success("Import done");
      // } catch(err) {
      //   console.error(err);
      //   this.$emit("import:error", err);
      //   if (err.response && err.response.data && err.response.data.message) {
      //     this.$toasted.error(`Import failed: ${err.response.data.message}`);
      //   } else {
      //     this.$toasted.error("Import failed");
      //   }
      // } finally {
      //   this.$emit("update:importing", false);
      // }
      try {
        this.$emit("update:importing", true);
        await this.$store.dispatch("importData", {
          vectorId: this.vectorId,
          formData: this.formData,
          rows: this.importData.rows,
          submit: true
        });
        this.$emit("import:success");
        this.$toasted.success("Import done");
      } catch(err) {
        console.error(err);
        this.$emit("import:error", err);
        if (err.response && err.response.data && err.response.data.message) {
          this.$toasted.error(`Import failed: ${err.response.data.message}`);
        } else {
          this.$toasted.error("Import failed");
        }
      } finally {
        this.$emit("update:importing", false);
      }
    },
    refreshData() {
      this.fileUploaded({
        url: this.formData.importExcel.path
      });
    },
    /*
     * The spreadsheet reader hands phone number columns back as raw cell text, and Excel's
     * General cell format has already dropped the leading "+" / "0" by then - so "+60124709699"
     * arrives as "60124709699" and would be stored (and displayed) under the workspace dial
     * code as "+6060124709699". Resolve each phone column to E.164 before it reaches the
     * preview grid, so what is previewed is what gets imported. (SS05112)
     */
    normalizePhoneNumbers(rows, information, columns) {
      if (!Array.isArray(rows) || !Array.isArray(information) || !Array.isArray(columns))
        return rows;

      const phoneColumns = columns
        .filter(column => column.fieldName && column.informationId)
        .map(column => {
          const item = information.find(x => x.id === column.informationId);
          /* 26 = Phone Number */
          if (!item || item.type !== 26) return null;
          return {
            fieldName: column.fieldName,
            country: countryCode(item.country) || this.defaultCountry
          };
        })
        .filter(Boolean);

      if (!phoneColumns.length) return rows;

      rows.forEach(row => {
        phoneColumns.forEach(column => {
          if (row[column.fieldName] != null && row[column.fieldName] !== "") {
            row[column.fieldName] = normalizePhoneNumber(row[column.fieldName], column.country);
          }
        });
      });

      return rows;
    },
    async fileUploaded(response) {
      try {
        this.uploading = true;
        // SS06146: what the request carries, so an edit made while it runs is not overwritten
        const sentColumns = JSON.stringify(this.formData.columns || []);
        const res = await this.$store.dispatch("importData", {
          url: response.url,
          vectorId: this.vectorId,
          formData: this.formData
        });
        this.importData.rows = this.normalizePhoneNumbers(
          res.data.rows,
          res.data.information,
          res.data.formData.columns
        );
        let canvasStore = canvasDesignStore;

        this.headerColumns.fields[1].optionList = res.data.information;
        this.informationSelection = res.data.information;
        this.mergeInformationSelection = res.data.information;
        this.mergeInformationSelection.unshift({ id: 'documentId', displayName: 'Document ID'})

        this.formData.columns = columnsAfterImport({
          userImport: this.userImport,
          sentSnapshot: sentColumns,
          current: this.formData.columns,
          returned: res.data.formData.columns
        });
        this.spreadsheetDataColumns = res.data.formData.columns.slice();
        this.spreadsheetDataColumns.push({
          fieldName: "error",
          displayName: "Error"
        });
        this.columns = {
          fieldName: "rows",
          fields: this.spreadsheetDataColumns
        };
      } catch (err) {
        console.error(err);
        this.$toasted.global.error({ response: err.response });
      } finally {
        this.uploading = false;
      }
    }
  },
  computed: {
    isBeta() {
      return (
        authorizationStore.getters.isBeta ||
        (this.$route.name === 'builder' || this.$route.name === 'studio' ? canvasDesignStore.getters.workspaceBeta : clientStore.getters.workspaceBeta)
      );
    },
    /* workspace country as an ISO code, the default for phone numbers without a dial code */
    defaultCountry() {
      return countryCode(
        this.$route.name === 'builder' || this.$route.name === 'studio'
          ? canvasDesignStore.getters.workspaceCountry
          : clientStore.getters.workspaceCountry
      );
    }
  },
  mounted() {
    let vm = this;
    vm.$store.dispatch("basicSetup", {
      baseServer: this.baseServer,
      token: JSON.parse(localStorage["ls.authorizationData"])["access_token"]
    });

    vm.$store.dispatch("setVector", {
      vectorId: this.vectorId
    });
  }
};
</script>

<style lang="scss" scoped>
.fts-import-designer {
  .import-refresh-btn {
    margin-top: 0.75rem;
  }
}
</style>